import { forwardRef, type SelectHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/cn'
import { ChevronDown } from 'lucide-react'

const dropdownVariants = cva(
  'w-full appearance-none rounded-md text-zinc-100 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        sm: 'px-2 py-1 text-xs',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-2.5 text-base',
        compact: 'px-2.5 py-1.5 text-sm',
      },
      variant: {
        default: 'bg-zinc-900 border border-zinc-800',
        glass: 'bg-white/5 border border-white/10 text-[#EEEEEE]',
      },
      hasError: {
        true: 'border-red-500/50 focus:border-red-500',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        hasError: false,
        className: 'focus:border-purple-500',
      },
      {
        variant: 'glass',
        hasError: false,
        className: 'focus:border-[#5E6AD2]/50',
      },
    ],
    defaultVariants: {
      size: 'md',
      variant: 'default',
      hasError: false,
    },
  }
)

export interface DropdownProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof dropdownVariants> {
  label?: ReactNode
  error?: string
  containerClassName?: string
  helperText?: string
  /** Left decorator (e.g., priority color dot) — rendered absolutely positioned */
  startDecorator?: ReactNode
}

export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (
    {
      label,
      error,
      className,
      containerClassName,
      hasError,
      size,
      variant,
      id,
      helperText,
      startDecorator,
      children,
      ...props
    },
    ref
  ) => {
    const showError = Boolean(error) || hasError
    const hasStartDecorator = Boolean(startDecorator)

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <label
            htmlFor={id}
            className="block text-xs font-medium text-zinc-400"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {hasStartDecorator && (
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10">
              {startDecorator}
            </div>
          )}
          <select
            id={id}
            ref={ref}
            className={cn(
              dropdownVariants({ size, variant, hasError: showError }),
              hasStartDecorator && 'pl-8',
              'pr-7', // Reserve space for chevron
              className
            )}
            {...props}
          >
            {children}
          </select>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <ChevronDown size={16} className={cn(variant === 'glass' ? 'text-[#666666]' : 'text-zinc-500')} />
          </div>
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        {helperText && !error && (
          <p className="text-[10px] text-zinc-500">{helperText}</p>
        )}
      </div>
    )
  }
)

Dropdown.displayName = 'Dropdown'



